import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { LoadingIndicator } from 'ui-components/components/loading-indicator';
import { Text, TextType } from 'ui-components/components/text';
import { noop } from 'ui-components/default-props/noop';

import { ConfigurationSection } from './configuration-section';
import { InventoryImportSection } from './inventory-import-section';
import { messages } from './messages';
import { SetupGuidePanel } from './setup-guide-panel';
import { SetupWarehouseSection } from './setup-warehouse-section';
import style from './style.module.less';
import { SellerFeatures } from '../../../enums/seller-features';
import { useBinaryState } from '../../../hooks/use-binary-state';
import { useIsSellerFeatureEnabled } from '../../../hooks/use-is-seller-feature-enabled';
import { useInventoryTrackingEnabled } from '../../../hooks/use-seller-setting';
import { InventoryService } from '../../../services/inventory-service';
import Global from '../../../store/global-selectors';
import { getInventory } from '../../../store/inventory/inventory.api';
import { SettingsPageTitle } from '../components/settings-page-title/settings-page-title';

const useSetup = () => {
  const isInventoryTrackingEnabled = useInventoryTrackingEnabled();
  const shipFroms = useSelector(Global.selectSellerShipFroms);
  const isWarehouseConnected = useSelector((state) =>
    shipFroms.some(({ shipFromId }) => InventoryService.doesShipFromHaveNativeInventoryTrackingEnabled(shipFromId, state)),
  );

  const isInventorySyncEnabled = useIsSellerFeatureEnabled(SellerFeatures.InventorySync);

  const [isLoading, startLoading, stopLoading] = useBinaryState(true);
  const [isAnyProductInInventory, setIsAnyProductInInventory] = useState(false);

  useEffect(() => {
    const inventoryCall = getInventory({ pageSize: 1 });

    startLoading();
    inventoryCall.promise
      .then((data) => setIsAnyProductInInventory(data.totalCount > 0))
      .catch(noop)
      .finally(stopLoading);

    return () => {
      inventoryCall.cancel();
      stopLoading();
    };
  }, [startLoading, stopLoading]);

  return {
    isLoading,
    isWarehouseConnected,
    isInventorySyncEnabled,
    isAnyProductInInventory,
    isInventoryTrackingEnabled,
  };
};

export const InventorySettingsPage = () => {
  const { isLoading, ...config } = useSetup();

  return (
    <div className={style.content}>
      <SettingsPageTitle value={messages.inventorySettingsHeader} />
      {isLoading ? (
        <LoadingIndicator className={style['loading-indicator']} />
      ) : (
        <div className={style['content-body']}>
          <div className={style['content-main']}>
            <Text textType={TextType.H2} value={messages.generalSectionHeader} />
            <section className={style['content-section']}>
              <ConfigurationSection />
            </section>
            <Text textType={TextType.H2} value={messages.setupWarehouseHeader} />
            <section className={style['content-section']}>
              <SetupWarehouseSection />
            </section>
            <Text textType={TextType.H2} value={messages.importSectionHeader} />
            <section className={style['last-content-section']}>
              <InventoryImportSection />
            </section>
          </div>
          <section className={style['setup-guide']}>
            <SetupGuidePanel {...config} />
          </section>
        </div>
      )}
    </div>
  );
};
